import { registerLocaleData } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import en from '@angular/common/locales/en';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { ApiModule } from '@buspro/web/apis';
import {
  authTokenInterceptor,
  httpErrorInterceptor,
  TANSTACK_SENSIBLE_DEFAULTS,
  TenantAuthService,
  tokenRefreshInterceptor,
  WEB_CONFIG,
} from '@buspro/web/core';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { provideLoadingBarRouter } from '@ngx-loading-bar/router';
import {
  QueryClient,
  provideTanStackQuery,
} from '@tanstack/angular-query-experimental';
import {
  AuthenticationProvider,
  LocalStorageProvider,
  provideNgxAuth,
  StorageProvider,
} from '@corelabs/ng-auth';
import { NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxPermissionsModule } from 'ngx-permissions';
import { environment } from '../environments/environment';
import { tenantRoutes } from './tenant.routes';
import { provideLoadingBar } from '@ngx-loading-bar/core';

registerLocaleData(en);

export const tenantConfig: ApplicationConfig = {
  providers: [
    // Auth providers must be provided BEFORE router and HTTP client
    // because guards and interceptors depend on them
    provideNgxAuth({
      authenticationProvider: {
        provide: AuthenticationProvider,
        useClass: TenantAuthService,
        deps: [],
      },
      storageProvider: {
        provide: StorageProvider,
        useClass: LocalStorageProvider,
      },
      homeUrl: '/dashboard',
      noAuthRedirectUrl: '/passport/login',
      sessionExpiredRedirectUrl: '/passport/login',
      authenticationHeader: 'Authorization',
      tokenType: 'Bearer',
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(tenantRoutes, withPreloading(PreloadAllModules)),
    provideHttpClient(
      withFetch(),
      // Order matters (#852). Errors travel right-to-left, so the error
      // interceptor is FIRST (outermost) and sees only what the refresh
      // interceptor could not rescue; it used to be innermost and sent every
      // 401 to the login page before a refresh was even tried.
      withInterceptors([
        httpErrorInterceptor,
        authTokenInterceptor,
        tokenRefreshInterceptor,
      ]),
      withInterceptorsFromDi(),
    ),
    provideTanStackQuery(new QueryClient({
      defaultOptions: {
        ...TANSTACK_SENSIBLE_DEFAULTS,
      }
    }), /**withDevtools()**/),
    provideAnimations(),
    provideFirebaseApp(() =>
      initializeApp({
        apiKey: 'AIzaSyAO0rzFPfL6u1mecuk5hoOHEQ7hjO6S5QM',
        authDomain: 'busprov2.firebaseapp.com',
        projectId: 'busprov2',
        storageBucket: 'busprov2.appspot.com',
        messagingSenderId: '1049134460984',
        appId: '1:1049134460984:web:718d09ec23d948b9b6b6cd',
        databaseURL: 'https://busprov2-default-rtdb.firebaseio.com',
        measurementId: 'G-Y67P8GECKJ',
      }),
    ),
    provideFirestore(() => getFirestore()),
    provideAnalytics(() => getAnalytics()),
    providePerformance(() => getPerformance()),

    // Dynamic icon loading is enabled via assets configuration in project.json
    // No need to provide static icons - they will be loaded on demand
    provideLoadingBarInterceptor(),
    provideLoadingBarRouter(),
    provideLoadingBar({ latencyThreshold: 100, }),

    importProvidersFrom([
      NzNotificationModule,
      NzModalModule,
      NgxPermissionsModule.forRoot(),
      ApiModule.forRoot({
        rootUrl: environment.server,
      }),
      LoggerModule.forRoot({
        level: NgxLoggerLevel.INFO,
        serverLogLevel: NgxLoggerLevel.ERROR,
      }),
    ]),
    {
      provide: Window,
      useValue: window,
    },
    { provide: NZ_I18N, useValue: en_US },
    { provide: WEB_CONFIG, useValue: environment },
  ],
};
